import { inject, injectable } from 'inversify'
import { Subscription } from 'rxjs'

import { InjectionTokens } from '@/controller/tokens'

import { UserChartSynced } from '@/domain/events/UserChartSynced'
import { Result } from '@/domain/protocols/Result'
import { EnsureUserChartTilesUseCase } from '@/domain/useCases/UserCharts'

import type { IUserChartRepository } from '@/data/UserChartRepository/IUserChartRepository'
import { hasUserChartTiles } from '@/data/UserChartRepository/userChartTileStore'

import { IService } from '../IService'

import type { ILogger } from '@/infra/logger/ILogger'

// How many charts to download in parallel. Tiles archives can be large (ENRC),
// so a small cap keeps the prefetch from saturating bandwidth / the server.
const PREFETCH_CONCURRENCY = 3

// Proactively downloads the processed tiles of every user chart into the
// in-memory tile store, so activating any chart on the map is instant — on any
// browser, not just the one where the chart was uploaded. Tiles are no longer
// persisted to IndexedDB (see EnsureUserChartTilesUseCase); this boot-time
// prefetch is what makes the feature browser-independent.
//
// Trigger: the chart list is populated by UserChartSyncService, which pulls from
// the server ~8s after boot and emits `UserChartSynced`. We subscribe to that
// event (and run once on start in case charts are already loaded), so the
// prefetch fires as soon as the list arrives. Re-syncs re-run it, downloading
// only the charts still missing from memory.
@injectable()
export class UserChartTilesPrefetchService implements IService {
  private syncedSubscription: Subscription | null = null
  private inflight: Promise<void> | null = null

  constructor(
    @inject(InjectionTokens.UserChartRepository) private repository: IUserChartRepository,
    @inject(InjectionTokens.Logger) private logger: ILogger
  ) {}

  init(): Result<void> {
    return Result.ok()
  }

  start(): Result<void> {
    this.syncedSubscription = UserChartSynced.subscribe(() => {
      void this.prefetchAll()
    })
    // Cover the case where charts were already loaded before this subscription.
    void this.prefetchAll()
    return Result.ok()
  }

  stop(): Result<void> {
    this.syncedSubscription?.unsubscribe()
    this.syncedSubscription = null
    return Result.ok()
  }

  // Best-effort: never throws. A single chart failing (e.g. network) does not
  // stop the others; it just won't be warm until the next sync/activation.
  async prefetchAll(): Promise<void> {
    if (this.inflight) return this.inflight
    this.inflight = this._runPrefetch()
    try {
      await this.inflight
    } finally {
      this.inflight = null
    }
  }

  private async _runPrefetch(): Promise<void> {
    const chartsResult = await this.repository.getCharts()
    if (chartsResult.isFailure) return

    const pending = chartsResult.getValue().filter((chart) => !hasUserChartTiles(chart.id.toString()))
    if (pending.length === 0) return

    let cursor = 0
    const worker = async (): Promise<void> => {
      while (cursor < pending.length) {
        const chart = pending[cursor++]
        const id = chart.id.toString()
        const result = await new EnsureUserChartTilesUseCase().execute(id)
        if (result.isFailure) {
          this.logger.warn('UserChartTilesPrefetchService', `prefetch failed for ${id}: ${result.error}`)
        }
      }
    }

    const workers = Array.from({ length: Math.min(PREFETCH_CONCURRENCY, pending.length) }, () => worker())
    await Promise.all(workers)
  }
}
